import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, Leaf, ArrowRight, Check, MapPin, Phone, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const prefill = location.state?.prefill || {};

  const [name, setName] = useState(prefill.name || '');
  const [phone, setPhone] = useState(prefill.phone || '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptAge, setAcceptAge] = useState(false);

  // OTP state
  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(0);

  const { sendOtp, phoneRegister } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatPhoneForDisplay = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.slice(0, 10);
  };

  const getE164Phone = () => `+91${phone.replace(/\D/g, '')}`;

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10 || !/^[6-9]/.test(digits)) {
      setError('Please enter a valid Indian mobile number (must start with 6, 7, 8, or 9)');
      return;
    }

    if (!acceptAge) {
      setError('You must confirm that you are 18 years of age or older to register.');
      return;
    }

    if (!acceptTerms) {
      setError('Please accept the terms and conditions');
      return;
    }

    setIsLoading(true);
    try {
      const result = await sendOtp(getE164Phone());
      if (result.success) {
        setStep('otp');
        setCountdown(result.cooldownSeconds || 60);
      } else {
        setError(result.error || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      console.error('Send OTP error:', err);
      setError('Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);
    try {
      const result = await phoneRegister(name, getE164Phone(), otp, undefined, new Date().toISOString());
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Register error:', err);
      setError('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setError('');
    setIsLoading(true);
    try {
      const result = await sendOtp(getE164Phone());
      if (result.success) {
        setCountdown(result.cooldownSeconds || 60);
        setOtp('');
      } else {
        setError(result.error || 'Failed to resend OTP.');
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[#1a2f0f] via-[#2e4e18] to-[#3f6b20] overflow-hidden">
        {/* Decorative background circles */}
        <div className="absolute -top-28 -right-28 w-[440px] h-[440px] rounded-full bg-white/5" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute top-1/3 right-8 w-40 h-40 rounded-full bg-[#FF9800]/10" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white h-full">
          {/* Logo */}
          <Link to="/">
            <img src="/logo.svg" alt="BookMyFresh" className="h-14 w-auto" />
          </Link>

          {/* Main content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-6">
              <Leaf className="w-4 h-4 text-[#8BC34A]" />
              <span className="text-sm font-medium text-white/90">Fresh Produce Delivered Daily</span>
            </div>
            <h1 className="text-4xl font-bold mb-4 leading-snug">
              Join BookMyFresh<br />today
            </h1>
            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-sm">
              Order fresh seasonal fruits and vegetables from farms and trusted suppliers — on your schedule.
            </p>

            <div className="space-y-3 max-w-sm">
              {[
                'Fresh from farms & trusted suppliers',
                'On-demand or scheduled delivery',
                'Serving Jaipur — more cities coming soon',
                'No minimum order for instant delivery',
              ].map((text) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#548B2E]/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-[#8BC34A]" />
                  </div>
                  <span className="text-white/85 text-sm">{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer note */}
          <p className="text-white/40 text-xs">
            By creating an account, you agree to our Terms &amp; Privacy Policy.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/shop" className="inline-flex items-center gap-2">
              <img src="/logo-symbol.png" alt="Book my fresh" className="h-10 w-auto" />
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Create Account</h2>
            <p className="text-gray-600 mb-4">
              {step === 'form' ? 'Start your journey to farm-fresh goodness.' : 'Verify your phone to complete registration.'}
            </p>

            {/* Jaipur Delivery Note */}
            <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-700 text-sm">
                <span className="font-medium">Note:</span> Currently, we deliver only to Jaipur locations.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {step === 'form' ? (
              <form onSubmit={handleSendOtp} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your full name"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#548B2E] focus:border-transparent transition-all"
                      required />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
                  <div className="relative flex">
                    <span className="inline-flex items-center px-4 py-3 border border-r-0 border-gray-300 rounded-l-xl bg-gray-50 text-gray-600 text-sm font-medium">+91</span>
                    <div className="relative flex-1">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input type="tel" value={phone} onChange={(e) => setPhone(formatPhoneForDisplay(e.target.value))}
                        placeholder="Enter 10-digit mobile number"
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-r-xl focus:ring-2 focus:ring-[#548B2E] focus:border-transparent transition-all"
                        maxLength={10} required />
                    </div>
                  </div>
                </div>

                {/* Age Declaration — mandatory */}
                <div className={`flex items-start gap-3 p-3 rounded-xl border ${acceptAge ? 'border-[#548B2E] bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
                  <input
                    id="accept-age"
                    type="checkbox"
                    checked={acceptAge}
                    onChange={(e) => setAcceptAge(e.target.checked)}
                    className="w-4 h-4 mt-0.5 flex-shrink-0 rounded border-gray-300 text-[#548B2E] focus:ring-[#548B2E]"
                  />
                  <label htmlFor="accept-age" className="text-sm text-gray-700 cursor-pointer select-none">
                    <span className="font-semibold text-gray-800">I confirm that I am 18 years of age or older.</span>
                    <span className="block text-xs text-gray-500 mt-0.5">Age verification is required to use BookMyFresh.</span>
                  </label>
                </div>

                <div className="flex items-start">
                  <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="w-4 h-4 mt-1 rounded border-gray-300 text-[#548B2E] focus:ring-[#548B2E]" />
                  <span className="ml-2 text-sm text-gray-600">
                    I agree to the{' '}
                    <Link to="/legal#terms" target="_blank" rel="noopener noreferrer" className="text-[#548B2E] hover:underline">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/legal#privacy" target="_blank" rel="noopener noreferrer" className="text-[#548B2E] hover:underline">Privacy Policy</Link>
                  </span>
                </div>

                <button id="send-otp-button" type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isLoading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Verify Phone & Register
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyAndRegister} className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Enter OTP</label>
                    <span className="text-sm text-gray-500">Sent to +91 {phone}</span>
                  </div>
                  <div className="relative">
                    <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" inputMode="numeric" value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#548B2E] focus:border-transparent transition-all text-center text-lg tracking-widest"
                      maxLength={6} autoFocus required />
                  </div>
                </div>

                <button type="submit" disabled={isLoading || otp.length !== 6}
                  className="w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isLoading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Verify & Create Account
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <div className="flex items-center justify-between text-sm">
                  <button type="button"
                    onClick={() => { setStep('form'); setOtp(''); setError(''); }}
                    className="text-gray-600 hover:text-gray-800">
                    ← Back to form
                  </button>
                  <button type="button" onClick={handleResendOtp}
                    disabled={countdown > 0 || isLoading}
                    className="text-[#548B2E] hover:text-[#3f6b20] font-medium disabled:text-gray-400 disabled:cursor-not-allowed">
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-8 text-center">
              <p className="text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-[#548B2E] hover:text-[#3f6b20] font-semibold">
                  Sign in
                </Link>
              </p>
            </div>

            {/* <div className="mt-4 pt-4 border-t border-gray-100 text-center">
              <Link
                to="/admin/login"
                className="text-xs text-gray-400 hover:text-[#548B2E] transition-colors"
              >
                Login as Admin
              </Link>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;